/**
 * Response Normalizer
 * 
 * Transforms document-style AI responses into clean, chat-native formatting.
 * Removes academic headings, converts numbered lists to bullets, limits bold,
 * and breaks long paragraphs into conversational chunks.
 */

export function normaliseAssistantText(text: string): string {
  if (!text || typeof text !== 'string') return text;

  let normalized = text;

  // 1. Remove "Section X:" headers (case-insensitive)
  normalized = normalized.replace(/^#{1,6}\s*Section\s+\d+\s*:?\s*/gim, '');
  normalized = normalized.replace(/^Section\s+\d+\s*:?\s*/gim, '');
  normalized = normalized.replace(/\*\*Section\s+\d+\s*:?\s*\*\*/gim, '');

  // 2. Remove common document-style headers
  normalized = normalized.replace(/^#{1,6}\s*(Core Components Analysis|Analysis|Overview|Summary|Introduction|Conclusion|Discussion)\s*:?\s*/gim, '');
  normalized = normalized.replace(/\*\*(Core Components Analysis|Analysis|Overview|Summary|Introduction|Conclusion|Discussion)\s*:?\s*\*\*/gim, '');

  // 3. Convert numbered lists (1. 2. 3.) to bullets if >3 items OR contains "Component:"/"Analysis"
  const numberedListPattern = /^(\d+\.\s+.*(?:\n|$))+/gm;
  const numberedMatches = normalized.match(numberedListPattern);
  if (numberedMatches) {
    numberedMatches.forEach(match => {
      const lines = match.split('\n').filter(l => l.trim());
      const shouldConvert = lines.length > 3 || 
                           match.match(/Component:|Analysis|Section/i);
      
      if (shouldConvert) {
        const bulleted = match.replace(/^\d+\.\s+/gm, '- ');
        normalized = normalized.replace(match, bulleted);
      }
    });
  }

  // 4. Demote markdown headings (H1-H6) to plain text labels
  normalized = normalized.replace(/^#{1,6}\s+(.+)$/gm, (match, content) => {
    // Convert to small label format (no markdown heading)
    return content.trim();
  });

  // 5. Clamp bold blocks - if entire line is bold, convert to plain text label
  normalized = normalized.replace(/^\*\*(.+?)\*\*\s*$/gm, (match, content) => {
    // If it's a heading-like pattern, remove bold
    if (content.match(/^(Section|Chapter|Part|Analysis|Overview|Summary)/i)) {
      return content.trim();
    }
    // Otherwise keep bold but reduce weight (will be handled by renderer)
    return match;
  });

  // 6. Break up long paragraphs (>320 chars) into 2-3 sentence chunks
  const paragraphs = normalized.split(/\n\n+/);
  const brokenParagraphs = paragraphs.map(para => {
    if (para.length > 320 && !para.match(/^[-*•]\s/m)) { // Don't break lists
      // Split on sentence boundaries (. ! ?)
      const sentences = para.match(/[^.!?]+[.!?]+/g) || [para];
      if (sentences.length > 1) {
        // Group into 2-3 sentence chunks
        const chunks: string[] = [];
        for (let i = 0; i < sentences.length; i += 2) {
          chunks.push(sentences.slice(i, i + 2).join(' ').trim());
        }
        return chunks.join('\n\n');
      }
    }
    return para;
  });
  normalized = brokenParagraphs.join('\n\n');

  // 7. Clean up excessive whitespace
  normalized = normalized.replace(/\n{3,}/g, '\n\n');
  normalized = normalized.trim();

  return normalized;
}

/**
 * Light normalization for streaming chunks (applied during stream)
 * Only removes obvious document patterns, full normalization happens at end
 */
export function normaliseStreamingChunk(chunk: string): string {
  if (!chunk || typeof chunk !== 'string') return chunk;

  let normalized = chunk;

  // Remove "Section X:" patterns as they appear
  normalized = normalized.replace(/Section\s+\d+\s*:?\s*/gi, '');

  // Remove common document headers
  normalized = normalized.replace(/\*\*(Core Components Analysis|Analysis|Overview)\s*:?\s*\*\*/gi, '');

  return normalized;
}
